/**
 * Synchro du catalogue (plan, PR 1.4) : au demarrage, chaque nuit a 3 h
 * (heure de Toronto) si l'app est ouverte, et sur demande.
 *
 * 1. GET /caisse/config : cle publique de signature du catalogue
 *    (catalog_signing_key { alg: 'ES256', kid, jwk }, PR 1.2). Gardee dans
 *    `meta` : si la configuration ne repond pas, la derniere cle connue sert.
 *    Aucune cle n'est inventee ni generee ici.
 * 2. GET /caisse/catalog avec If-None-Match = l'ETag du serveur, refait a
 *    partir de l'instantane en place ("<snapshot_id>.<kid>", routes/caisse/index.js).
 *    304 : le catalogue en place est confirme a jour.
 * 3. 200 : la signature ECDSA P-256 / SHA-256 du serveur est verifiee sur la
 *    forme canonique (sync/canonicalJson.js), puis l'empreinte SHA-256 et le
 *    contenu, AVANT de toucher au catalogue en place.
 * 4. Remplacement atomique (store/catalog.js replaceCatalog).
 *
 * Tout echec (reseau coupe, telechargement interrompu, signature invalide,
 * serveur sans cle, poste suspendu) laisse l'ancien catalogue intact ; le
 * resultat dit pourquoi, et `meta.catalog_sync` le garde pour l'ecran.
 */
import { getMeta, putMeta } from '../store/meta';
import { getCatalogMeta, markCatalogConfirmed, replaceCatalog } from '../store/catalog';
import { META_KEYS } from '../store/schema';
import { canonicalize } from './canonicalJson';

export const CATALOG_FORMATS = Object.freeze([2]);
export const TAX_CODES = Object.freeze(['NONE', 'TPS', 'TPS_TVQ']);
const SNAPSHOT_ID_PATTERN = /^[0-9a-f]{64}$/;
const BASE64URL_PATTERN = /^[A-Za-z0-9_-]+$/;
const VERIFY_ALGORITHM = Object.freeze({ name: 'ECDSA', hash: 'SHA-256' });
const KEY_ALGORITHM = Object.freeze({ name: 'ECDSA', namedCurve: 'P-256' });

export class CatalogSyncError extends Error {
    constructor(message, code) {
        super(message);
        this.name = 'CatalogSyncError';
        this.code = code;
    }
}

/** ETag que le serveur donne a un instantane, refait sans lire l'en-tete. */
export function catalogEtag(meta) {
    return meta && meta.snapshot_id && meta.kid ? `"${meta.snapshot_id}.${meta.kid}"` : null;
}

function base64UrlToBytes(text) {
    if (typeof text !== 'string' || !BASE64URL_PATTERN.test(text)) {
        throw new CatalogSyncError('Signature mal formee', 'SIGNATURE');
    }
    const b64 = text.replace(/-/g, '+').replace(/_/g, '/');
    const binary = atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4));
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return bytes;
}

function toHex(buffer) {
    return Array.from(new Uint8Array(buffer), (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Verifie un catalogue recu (signature, empreinte, contenu). Ne touche a rien.
 * @returns {Promise<{ catalog: object, snapshotId: string, kid: string }>}
 * @throws {CatalogSyncError} SIGNATURE ou CATALOGUE_INVALIDE
 */
export async function verifySignedCatalog({ body, signingKey, storeId, subtle = globalThis.crypto && globalThis.crypto.subtle }) {
    if (!subtle) throw new CatalogSyncError('WebCrypto indisponible', 'SIGNATURE');
    if (!body || typeof body !== 'object' || !body.catalog || typeof body.catalog !== 'object') {
        throw new CatalogSyncError('Catalogue mal forme', 'CATALOGUE_INVALIDE');
    }
    const { snapshot_id: snapshotId, signature, catalog } = body;
    if (typeof snapshotId !== 'string' || !SNAPSHOT_ID_PATTERN.test(snapshotId)) {
        throw new CatalogSyncError('Identifiant d\'instantane invalide', 'CATALOGUE_INVALIDE');
    }
    if (!signature || signature.alg !== 'ES256' || typeof signature.kid !== 'string') {
        throw new CatalogSyncError('Signature absente', 'SIGNATURE');
    }
    if (!signingKey || signature.kid !== signingKey.kid) {
        throw new CatalogSyncError('Catalogue signe par une autre cle', 'SIGNATURE');
    }

    let canonical;
    try {
        canonical = canonicalize(catalog);
    } catch (_error) {
        throw new CatalogSyncError('Catalogue non canonique', 'SIGNATURE');
    }
    const bytes = new TextEncoder().encode(canonical);
    const sig = base64UrlToBytes(signature.value);
    let valid = false;
    try {
        const { kty, crv, x, y } = signingKey.jwk;
        const key = await subtle.importKey('jwk', { kty, crv, x, y, ext: true }, KEY_ALGORITHM, false, ['verify']);
        valid = await subtle.verify(VERIFY_ALGORITHM, key, sig, bytes);
    } catch (_error) {
        valid = false;
    }
    if (!valid) throw new CatalogSyncError('Signature du catalogue invalide', 'SIGNATURE');

    const digest = toHex(await subtle.digest('SHA-256', bytes));
    if (digest !== snapshotId) throw new CatalogSyncError('Empreinte du catalogue differente', 'SIGNATURE');

    // Contenu : signe par le serveur, mais verifie quand meme avant de remplacer.
    if (!CATALOG_FORMATS.includes(catalog.format)) {
        throw new CatalogSyncError(`Format de catalogue inconnu : ${catalog.format}`, 'CATALOGUE_INVALIDE');
    }
    if (catalog.store_id !== storeId) {
        throw new CatalogSyncError('Catalogue d\'un autre magasin', 'CATALOGUE_INVALIDE');
    }
    if (!Array.isArray(catalog.items) || catalog.item_count !== catalog.items.length) {
        throw new CatalogSyncError('Nombre d\'articles incoherent', 'CATALOGUE_INVALIDE');
    }
    const seen = new Set();
    for (const item of catalog.items) {
        if (!item || typeof item.productcode !== 'string' || item.productcode === '' || seen.has(item.productcode)
            || typeof item.name !== 'string' || !Number.isSafeInteger(item.price_cents)
            || !TAX_CODES.includes(item.tax_code)) {
            throw new CatalogSyncError('Article invalide dans le catalogue', 'CATALOGUE_INVALIDE');
        }
        seen.add(item.productcode);
    }
    return { catalog, snapshotId, kid: signature.kid };
}

/** Code d'erreur de la synchro pour une erreur de l'API. */
export function codeForApiError(error) {
    if (!error) return 'ERREUR';
    if (error.kind === 'network' && error.code === 'TELECHARGEMENT_INTERROMPU') return 'TELECHARGEMENT';
    if (error.kind === 'network' || error.kind === 'timeout') return 'HORS_LIGNE';
    if (error.kind === 'invalid') return error.code === 'TELECHARGEMENT_INCOMPLET' ? 'TELECHARGEMENT' : 'CATALOGUE_INVALIDE';
    if (error.kind === 'http') {
        if (error.status === 403 && error.code === 'POSTE_DESACTIVE') return 'POSTE_SUSPENDU';
        if (error.status === 403 && error.code === 'POSTE_REVOQUE') return 'POSTE_REVOQUE';
        if (error.status === 401) return 'POSTE_INCONNU';
        if (error.status === 503 && error.code === 'CATALOGUE_NON_SIGNE') return 'CATALOGUE_NON_SIGNE';
        if (error.status === 429) return 'TROP_DE_DEMANDES';
        if (error.status >= 500) return 'HORS_LIGNE';
    }
    return 'ERREUR';
}

/** Codes qui disent quelque chose du statut du poste. */
export const TERMINAL_STATUS_FOR_CODE = Object.freeze({
    POSTE_SUSPENDU: 'DISABLED',
    POSTE_REVOQUE: 'REVOKED',
    POSTE_INCONNU: 'UNKNOWN',
});

async function recordSync(db, { at, error, success }) {
    const previous = (await getMeta(db, META_KEYS.CATALOG_SYNC)) || {};
    await putMeta(db, {
        [META_KEYS.CATALOG_SYNC]: {
            last_attempt_at: at,
            last_success_at: success ? at : (previous.last_success_at || null),
            last_error: error || null,
        },
    });
}

/**
 * Cle publique de signature : celle de GET /caisse/config si elle repond
 * (gardee ensuite dans `meta`), sinon la derniere connue.
 */
async function resolveSigningKey({ db, api, token, at }) {
    try {
        const config = await api.getConfig(token);
        const entries = { [META_KEYS.CAISSE_CONFIG]: { store: config.store, config: config.config, fetched_at: at } };
        if (config.catalog_signing_key) entries[META_KEYS.CATALOG_SIGNING_KEY] = config.catalog_signing_key;
        await putMeta(db, entries);
        return config.catalog_signing_key || null;
    } catch (error) {
        const code = codeForApiError(error);
        if (TERMINAL_STATUS_FOR_CODE[code] || code === 'HORS_LIGNE') throw new CatalogSyncError(error.message, code);
        return (await getMeta(db, META_KEYS.CATALOG_SIGNING_KEY)) || null;
    }
}

/**
 * @param {object} options
 * @param {IDBDatabase} options.db
 * @param {object} options.api client caisseApi
 * @param {string} options.token jeton du poste
 * @param {number} options.storeId magasin du poste
 * @param {() => Date} [options.now]
 * @param {SubtleCrypto} [options.subtle]
 * @param {(p: { step: string, percent: number }) => void} [options.onProgress]
 * @returns {Promise<{ ok: true, status: 'updated'|'confirmed', snapshotId: string, itemCount?: number }
 *   | { ok: false, code: string }>}
 */
export async function syncCatalog({ db, api, token, storeId, now = () => new Date(), subtle, onProgress = () => {} }) {
    const startedAt = now().toISOString();
    try {
        onProgress({ step: 'config', percent: 5 });
        const signingKey = await resolveSigningKey({ db, api, token, at: startedAt });

        const current = await getCatalogMeta(db);
        // Cle changee : l'ETag ne correspond plus, on demande tout.
        const etag = current && signingKey && current.kid === signingKey.kid ? catalogEtag(current) : null;

        onProgress({ step: 'download', percent: 15 });
        let result;
        try {
            result = await api.getCatalog(token, { etag });
        } catch (error) {
            throw new CatalogSyncError(error && error.message, codeForApiError(error));
        }

        if (result.notModified) {
            const at = now().toISOString();
            const confirmed = current
                ? await markCatalogConfirmed(db, { snapshotId: current.snapshot_id, kid: current.kid, at })
                : false;
            if (!confirmed) throw new CatalogSyncError('Reponse 304 sans catalogue en place', 'CATALOGUE_INVALIDE');
            await recordSync(db, { at, success: true });
            onProgress({ step: 'done', percent: 100 });
            return { ok: true, status: 'confirmed', snapshotId: current.snapshot_id };
        }

        if (!signingKey) throw new CatalogSyncError('Aucune cle pour verifier le catalogue', 'CLE_ABSENTE');
        onProgress({ step: 'verify', percent: 70 });
        const { catalog, snapshotId, kid } = await verifySignedCatalog({ body: result.body, signingKey, storeId, subtle });

        onProgress({ step: 'save', percent: 85 });
        const at = now().toISOString();
        await replaceCatalog(db, {
            items: catalog.items,
            meta: {
                snapshot_id: snapshotId,
                kid,
                format: catalog.format,
                store_id: catalog.store_id,
                item_count: catalog.item_count,
                weight_barcode_prefix: catalog.weight_barcode_prefix === undefined ? null : catalog.weight_barcode_prefix,
                loaded_at: at,
                confirmed_at: at,
            },
        });
        await recordSync(db, { at, success: true });
        onProgress({ step: 'done', percent: 100 });
        return { ok: true, status: 'updated', snapshotId, itemCount: catalog.item_count };
    } catch (error) {
        const code = error instanceof CatalogSyncError ? error.code : 'ERREUR';
        try {
            await recordSync(db, { at: startedAt, error: code });
        } catch (_ignored) {
            // Le resultat suffit a l'ecran.
        }
        return { ok: false, code };
    }
}
